/**
 * API Error Handler Utility
 * Handles API errors according to the unified API error structure
 * See docs/api-errors.md for details
 */

/**
 * Extract error from API response or axios error
 * @param {Error|Object} error - Axios error or API response
 * @returns {Object|null} Error object with code, message, and fields (if applicable)
 */
export const extractApiError = (error) => {
  // Check if it's an axios error with response.data
  if (error.response?.data) {
    const response = error.response.data;
    
    // Check if response follows the unified structure
    if (typeof response === 'object' && 'success' in response && 'error' in response) {
      if (!response.success && response.error) {
        return response.error;
      }
    }
    
    // Fallback: check if error is directly in response.data
    if (response.error) {
      return response.error;
    }
    
    // Legacy fallback: check for message
    if (response.message) {
      return {
        code: 'unknown_error',
        message: response.message,
      };
    }
  }
  
  // Network or other errors
  if (error.message) {
    return {
      code: 'network_error',
      message: error.message || 'Network error occurred',
    };
  }
  
  return null;
};

/**
 * Handle API error and return appropriate error message
 * @param {Error|Object} error - Axios error or API response
 * @returns {Object} { generalError: string|null, fieldErrors: Record<string, string> }
 */
export const handleApiError = (error) => {
  const apiError = extractApiError(error);
  
  if (!apiError) {
    return {
      generalError: 'An unexpected error occurred',
      fieldErrors: {},
    };
  }
  
  // Handle field validation errors
  if (apiError.code === 'field_validation_errors' && apiError.fields) {
    const fieldErrors = {};
    apiError.fields.forEach((fieldError) => {
      fieldErrors[fieldError.field] = fieldError.message;
    });
    
    return {
      generalError: null,
      fieldErrors,
    };
  }
  
  // Return general error message
  return {
    generalError: apiError.message || 'An error occurred',
    fieldErrors: {},
  };
};

/**
 * Get user-friendly error message for specific error codes
 * @param {string} code - Error code
 * @param {string} defaultMessage - Default message if code not found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (code, defaultMessage = 'An error occurred') => {
  const errorMessages = {
    invalid_credentials: 'Invalid email or password',
    email_already_exists: 'This email is already registered',
    current_password_incorrect: 'Current password is incorrect',
    new_password_same_as_current: 'New password must be different from current password',
    new_email_same_as_current: 'New email must be different from current email',
    new_email_already_exists: 'This email is already in use',
    invalid_or_expired_token: 'Invalid or expired token',
    password_reset_token_not_found: 'Password reset link is invalid',
    password_reset_token_expired: 'Password reset link has expired',
    password_reset_token_already_used: 'This password reset link has already been used',
    user_not_found: 'User not found',
    rate_limit_exceeded: 'Too many requests. Please try again later',
    internal_error: 'An internal error occurred. Please try again later',
    network_error: 'Network error. Please check your connection',
  };
  
  return errorMessages[code] || defaultMessage;
};

/**
 * Handle API error with user-friendly messages
 * @param {Error|Object} error - Axios error or API response
 * @returns {Object} { generalError: string|null, fieldErrors: Record<string, string> }
 */
export const handleApiErrorWithMessages = (error) => {
  const apiError = extractApiError(error);
  
  if (!apiError) {
    return {
      generalError: 'An unexpected error occurred',
      fieldErrors: {},
    };
  }
  
  // Handle field validation errors
  if (apiError.code === 'field_validation_errors' && apiError.fields) {
    const fieldErrors = {};
    apiError.fields.forEach((fieldError) => {
      fieldErrors[fieldError.field] = fieldError.message;
    });
    
    return {
      generalError: null,
      fieldErrors,
    };
  }
  
  // Map email_already_exists to email field error
  if (apiError.code === 'email_already_exists') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    return {
      generalError: null,
      fieldErrors: {
        email: userMessage,
      },
    };
  }
  
  // Map invalid_credentials to both email and password field errors
  if (apiError.code === 'invalid_credentials') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    return {
      generalError: null,
      fieldErrors: {
        email: userMessage,
        password: userMessage,
      },
    };
  }
  
  // Map current_password_incorrect to currentPassword or password field error
  // Check the request path to determine which field to use
  if (apiError.code === 'current_password_incorrect') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    // Check if this is from email change endpoint (change-email) or password change endpoint
    const requestUrl = error?.config?.url || 
                      error?.response?.config?.url || 
                      error?.response?.request?.responseURL || 
                      '';
    const isEmailChange = requestUrl.includes('change-email');
    return {
      generalError: null,
      fieldErrors: {
        [isEmailChange ? 'password' : 'currentPassword']: userMessage,
      },
    };
  }

  // Map new_password_same_as_current to newPassword field error
  if (apiError.code === 'new_password_same_as_current') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    return {
      generalError: null,
      fieldErrors: {
        newPassword: userMessage,
      },
    };
  }

  // Map new_email_same_as_current to newEmail field error
  if (apiError.code === 'new_email_same_as_current') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    return {
      generalError: null,
      fieldErrors: {
        new_email: userMessage,
      },
    };
  }

  // Map new_email_already_exists to newEmail field error
  if (apiError.code === 'new_email_already_exists') {
    const userMessage = getErrorMessage(apiError.code, apiError.message);
    return {
      generalError: null,
      fieldErrors: {
        new_email: userMessage,
      },
    };
  }
  
  // Get user-friendly message for the error code
  const userMessage = getErrorMessage(apiError.code, apiError.message);
  
  return {
    generalError: userMessage,
    fieldErrors: {},
  };
};
